import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Sun, Moon, Droplets, Sparkles, ShoppingBag, Save, CheckCircle, ShoppingCart, Calendar, Lightbulb, Pill, Volume2 } from 'lucide-react';
import { TextToSpeech } from '../components/voice';

// Generate speech text for TTS
const generateDiagnosisSpeech = (diagnosis, language) => {
  if (!diagnosis) return '';
  
  const isFr = language === 'fr';
  let speech = '';
  
  // Introduction
  speech += isFr 
    ? `Bonjour ${diagnosis.client_name || 'cher client'}. Voici votre analyse personnalisée de la Pharmacie Saint Ange des Amis. `
    : `Hello ${diagnosis.client_name || 'dear client'}. Here is your personalized analysis from Saint Ange Pharmacy. `;
  
  // Skin type
  if (diagnosis.skin_type) {
    const skinTypes = {
      dry: isFr ? 'sèche' : 'dry',
      oily: isFr ? 'grasse' : 'oily',
      combination: isFr ? 'mixte' : 'combination',
      normal: isFr ? 'normale' : 'normal'
    };
    speech += isFr 
      ? `Votre type de peau est ${skinTypes[diagnosis.skin_type] || diagnosis.skin_type}. `
      : `Your skin type is ${skinTypes[diagnosis.skin_type] || diagnosis.skin_type}. `;
  }
  
  // Score
  if (diagnosis.skin_score) {
    speech += isFr 
      ? `Votre score de santé de peau est de ${diagnosis.skin_score} sur 100. `
      : `Your skin health score is ${diagnosis.skin_score} out of 100. `;
  }
  
  // Morning routine
  if (diagnosis.morning_routine?.length > 0) {
    speech += isFr ? 'Pour votre routine du matin: ' : 'For your morning routine: ';
    diagnosis.morning_routine.forEach((step, i) => {
      const name = isFr ? step.name_fr : step.name_en;
      speech += `${i + 1}, ${name}. `;
    });
  }
  
  // Evening routine
  if (diagnosis.evening_routine?.length > 0) {
    speech += isFr ? 'Pour votre routine du soir: ' : 'For your evening routine: ';
    diagnosis.evening_routine.forEach((step, i) => {
      const name = isFr ? step.name_fr : step.name_en;
      speech += `${i + 1}, ${name}. `;
    });
  }
  
  // Tips
  if (diagnosis.skin_tips?.length > 0) {
    speech += isFr ? 'Quelques conseils importants: ' : 'Some important tips: ';
    diagnosis.skin_tips.slice(0, 3).forEach(tip => {
      const tipText = isFr ? tip.tip_fr : (tip.tip_en || tip.tip_fr);
      speech += `${tipText}. `;
    });
  }
  
  // Supplements
  if (diagnosis.supplements?.length > 0) {
    speech += isFr 
      ? 'Pour renforcer votre peau de l\'intérieur, nous vous recommandons: ' 
      : 'To strengthen your skin from within, we recommend: ';
    diagnosis.supplements.forEach(supp => {
      const name = isFr ? supp.name_fr : supp.name_en;
      speech += `${name}. `;
    });
  }
  
  // Closing
  speech += isFr 
    ? 'Nous vous remercions de votre confiance. N\'hésitez pas à nous consulter pour vos produits. À bientôt!' 
    : 'Thank you for your trust. Feel free to consult us for your products. See you soon!';
  
  return speech;
};

const DiagnosisResultsPage = ({ diagnosis, onBack, onViewProducts, onSave, onRecordSale, isAgent }) => {
  const { t, language } = useLanguage();

  const getConcernLabel = (concern) => {
    const labels = {
      acne: t('acne'),
      dark_spots: t('dark_spots'),
      wrinkles: t('wrinkles'),
      dehydration: t('dehydration'),
      sensitivity: t('sensitivity'),
      none: t('none')
    };
    return labels[concern] || concern;
  };

  const getSkinTypeLabel = (type) => {
    const labels = {
      dry: t('dry'),
      oily: t('oily'),
      combination: t('combination'),
      normal: t('normal')
    };
    return labels[type] || type;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-400 to-emerald-500';
    if (score >= 60) return 'from-yellow-400 to-amber-500';
    return 'from-orange-400 to-red-500';
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-28 sm:pb-32">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#0F172A] transition-colors"
            data-testid="results-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl text-[#0F172A] mx-auto pr-7">{t('yourResults')}</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Skin Score Card */}
        <Card className="border-[#E7E5E4] overflow-hidden animate-fade-in-up">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-[#E0F2FE] to-[#BAE6FD] p-6">
              <div className="flex items-center gap-6">
                {/* Score Circle */}
                <div className="relative w-28 h-28 flex-shrink-0">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke="#E7E5E4"
                      strokeWidth="8"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke="#0EA5E9"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${(diagnosis.skin_score / 100) * 314} 314`}
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${getScoreColor(diagnosis.skin_score)}`}>
                      {diagnosis.skin_score}
                    </span>
                    <span className="text-xs text-[#78716C]">/100</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-[#0F172A]">{t('skinScore')}</h2>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge className="badge-medical">{getSkinTypeLabel(diagnosis.skin_type)}</Badge>
                    </div>
                    <p className="text-sm text-[#78716C]">
                      {t('mainConcern')}: <span className="font-medium text-[#0F172A]">{getConcernLabel(diagnosis.main_concern)}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Concerns breakdown */}
            <div className="p-6 space-y-4">
              <h3 className="text-sm font-medium text-[#78716C] uppercase tracking-wider">
                {language === 'fr' ? 'Analyse détaillée' : 'Detailed Analysis'}
              </h3>
              {Object.entries(diagnosis.concerns).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#0F172A]">{getConcernLabel(key)}</span>
                    <span className="text-[#78716C]">{value}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Morning Routine */}
        <Card className="border-[#E7E5E4] animate-fade-in-up stagger-1" data-testid="morning-routine-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Sun className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">{t('morningRoutine')}</h3>
            </div>

            <div className="space-y-4">
              {diagnosis.morning_routine.map((step, idx) => (
                <div key={idx} className="routine-step">
                  <div className="routine-step-number">{step.step}</div>
                  <div className="pl-2">
                    <p className="font-medium text-[#0F172A]">
                      {language === 'fr' ? step.name_fr : step.name_en}
                    </p>
                    <p className="text-sm text-[#78716C]">
                      {language === 'fr' ? step.description_fr : step.description_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Evening Routine */}
        <Card className="border-[#E7E5E4] animate-fade-in-up stagger-2" data-testid="evening-routine-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Moon className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">{t('eveningRoutine')}</h3>
            </div>

            <div className="space-y-4">
              {diagnosis.evening_routine.map((step, idx) => (
                <div key={idx} className="routine-step">
                  <div className="routine-step-number">{step.step}</div>
                  <div className="pl-2">
                    <p className="font-medium text-[#0F172A]">
                      {language === 'fr' ? step.name_fr : step.name_en}
                    </p>
                    <p className="text-sm text-[#78716C]">
                      {language === 'fr' ? step.description_fr : step.description_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Active Ingredients */}
        <Card className="border-[#E7E5E4] animate-fade-in-up stagger-3" data-testid="ingredients-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#E0F2FE] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#0EA5E9]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">{t('activeIngredients')}</h3>
            </div>

            <div className="grid gap-3">
              {diagnosis.active_ingredients.map((ingredient, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 p-3 rounded-xl bg-[#FAFAF9] border border-[#E7E5E4]"
                >
                  <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0F172A]">
                      {language === 'fr' ? ingredient.name : ingredient.name_en}
                    </p>
                    <p className="text-sm text-[#78716C]">
                      {language === 'fr' ? ingredient.benefit_fr : ingredient.benefit_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Routine */}
        {diagnosis.weekly_routine && diagnosis.weekly_routine.length > 0 && (
          <Card className="border-[#E7E5E4] animate-fade-in-up stagger-4" data-testid="weekly-routine-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A]">
                  {language === 'fr' ? 'Soins hebdomadaires' : 'Weekly Care'}
                </h3>
              </div>

              <div className="space-y-4">
                {diagnosis.weekly_routine.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-purple-700">
                          {item.type === 'exfoliant' ? 'G' : 'M'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-[#0F172A]">
                            {language === 'fr' ? item.name_fr : item.name_en}
                          </p>
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {language === 'fr' ? item.frequency_fr : item.frequency_en}
                          </Badge>
                        </div>
                        <p className="text-sm text-[#78716C]">
                          {language === 'fr' ? item.description_fr : item.description_en}
                        </p>
                        {item.tip_fr && (
                          <p className="text-xs text-purple-600 mt-2 italic">
                            💡 {language === 'fr' ? item.tip_fr : (item.tip_en || item.tip_fr)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Skin Tips for Black Skin */}
        {diagnosis.skin_tips && diagnosis.skin_tips.length > 0 && (
          <Card className="border-[#E7E5E4] animate-fade-in-up stagger-5 border-amber-200 bg-amber-50/50" data-testid="skin-tips-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A]">
                  {language === 'fr' ? 'Conseils pour votre peau' : 'Tips for Your Skin'}
                </h3>
              </div>

              <div className="space-y-3">
                {diagnosis.skin_tips.map((tip, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-3 rounded-lg bg-white border border-amber-100"
                  >
                    <span className="text-lg">💡</span>
                    <p className="text-sm text-[#0F172A]">
                      {language === 'fr' ? tip.tip_fr : (tip.tip_en || tip.tip_fr)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dietary Supplements */}
        {diagnosis.supplements && diagnosis.supplements.length > 0 && (
          <Card className="border-[#E7E5E4] animate-fade-in-up stagger-6 border-green-200 bg-green-50/30" data-testid="supplements-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Pill className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">
                    {language === 'fr' ? 'Compléments alimentaires' : 'Dietary Supplements'}
                  </h3>
                  <p className="text-xs text-[#78716C]">
                    {language === 'fr' ? 'Pour renforcer votre peau de l\'intérieur' : 'To strengthen your skin from within'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {diagnosis.supplements.map((supplement, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 rounded-xl bg-white border border-green-100 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                        <Pill className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#0F172A]">
                          {language === 'fr' ? supplement.name_fr : supplement.name_en}
                        </p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-[#78716C]">
                            <span className="font-medium text-green-700">
                              {language === 'fr' ? 'Posologie: ' : 'Dosage: '}
                            </span>
                            {language === 'fr' ? supplement.dosage_fr : supplement.dosage_en}
                          </p>
                          <p className="text-sm text-[#78716C]">
                            <span className="font-medium text-green-700">
                              {language === 'fr' ? 'Bienfait: ' : 'Benefit: '}
                            </span>
                            {language === 'fr' ? supplement.benefit_fr : supplement.benefit_en}
                          </p>
                          <Badge className="bg-green-100 text-green-700 text-xs mt-2">
                            {language === 'fr' ? supplement.duration_fr : supplement.duration_en}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 rounded-lg bg-green-100/50 border border-green-200">
                <p className="text-xs text-green-700 text-center">
                  {language === 'fr' 
                    ? '⚠️ Consultez un professionnel de santé avant de commencer toute supplémentation'
                    : '⚠️ Consult a healthcare professional before starting any supplementation'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Listen to recommendations - Text to Speech */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">
                    {language === 'fr' ? 'Écouter les recommandations' : 'Listen to recommendations'}
                  </h3>
                  <p className="text-xs text-[#78716C]">
                    {language === 'fr' ? 'L\'IA lit vos conseils à voix haute' : 'AI reads your tips out loud'}
                  </p>
                </div>
              </div>
              <TextToSpeech 
                text={generateDiagnosisSpeech(diagnosis, language)}
                language={language}
                variant="button"
                size="md"
                showVoiceSelector={true}
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          {isAgent && !diagnosis.has_sale ? (
            <>
              <Button
                variant="outline"
                onClick={onViewProducts}
                className="flex-1"
                data-testid="view-products-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {t('viewProducts')}
              </Button>
              <Button
                onClick={onRecordSale}
                className="flex-1 btn-primary bg-green-600 hover:bg-green-700"
                data-testid="record-sale-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Enregistrer vente' : 'Record Sale'}
              </Button>
            </>
          ) : isAgent && diagnosis.has_sale ? (
            <div className="w-full p-4 bg-green-50 rounded-xl border border-green-200 text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-green-700 font-medium">
                {language === 'fr' ? 'Vente enregistrée' : 'Sale recorded'}
              </p>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onSave}
                className="flex-1"
                data-testid="save-diagnosis-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {t('saveDiagnosis')}
              </Button>
              <Button
                onClick={onViewProducts}
                className="flex-1 btn-primary"
                data-testid="view-products-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {t('viewProducts')}
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DiagnosisResultsPage;
